/**
 * fresh-browser-session
 *
 * Resolves browser Supabase sessions that are safe for server cookie sync
 * and private Realtime joins.
 *
 * Primary responsibilities:
 * - Refresh sessions before they expire
 * - Return one fresh token source for browser and server auth paths
 *
 * Dependencies:
 * - @supabase/supabase-js
 */

import type { Session, SupabaseClient, User } from '@supabase/supabase-js'

import type { Database } from '@/supabase/types'

export type BrowserAuthSession = Pick<Session, 'access_token' | 'expires_at'> &
  Partial<Pick<Session, 'refresh_token'>> & {
    user?: Pick<User, 'id'> | null
  }

type BrowserAuthSessionClient = Pick<SupabaseClient<Database>, 'auth'>

interface FreshBrowserSessionOptions<
  TSession extends BrowserAuthSession = BrowserAuthSession
> {
  onError?: (message: string, error: unknown) => void
  session?: TSession | null
}

const BROWSER_AUTH_REFRESH_WINDOW_SECONDS = 5 * 60

let pendingStoredSessionRefresh: Promise<BrowserAuthSession | null> | null =
  null

// --------------

function hasExplicitSession(options: FreshBrowserSessionOptions) {
  return Object.prototype.hasOwnProperty.call(options, 'session')
}

// --------------

function shouldRefreshBrowserSession(
  session: BrowserAuthSession | null | undefined
) {
  if (!session?.access_token?.trim()) {
    return true
  }

  // Unknown expiry cannot prove the token is safe after idle.
  if (typeof session.expires_at !== 'number') {
    return true
  }

  const nowInSeconds = Math.floor(Date.now() / 1000)

  return (
    session.expires_at - nowInSeconds <= BROWSER_AUTH_REFRESH_WINDOW_SECONDS
  )
}

// --------------

async function readStoredSession(
  supabase: BrowserAuthSessionClient,
  onError?: FreshBrowserSessionOptions['onError']
) {
  try {
    const { data, error } = await supabase.auth.getSession()

    if (error) {
      onError?.('Failed to read browser auth session', error)
      return null
    }

    return data.session ?? null
  } catch (error) {
    onError?.('Failed to read browser auth session', error)
    return null
  }
}

// --------------

async function refreshStoredSessionOnce(
  supabase: BrowserAuthSessionClient,
  onError?: FreshBrowserSessionOptions['onError']
) {
  try {
    const { data, error } = await supabase.auth.refreshSession()

    if (error) {
      onError?.('Failed to refresh browser auth session', error)
      return null
    }

    return data.session ?? null
  } catch (error) {
    onError?.('Failed to refresh browser auth session', error)
    return null
  }
}

// --------------

async function refreshStoredSession(
  supabase: BrowserAuthSessionClient,
  onError?: FreshBrowserSessionOptions['onError']
) {
  if (pendingStoredSessionRefresh) {
    return pendingStoredSessionRefresh
  }

  pendingStoredSessionRefresh = refreshStoredSessionOnce(
    supabase,
    onError
  ).finally(() => {
    pendingStoredSessionRefresh = null
  })

  return pendingStoredSessionRefresh
}

// --------------

export async function getFreshBrowserSession(
  supabase: BrowserAuthSessionClient
): Promise<Session | null>
export async function getFreshBrowserSession(
  supabase: BrowserAuthSessionClient,
  options: FreshBrowserSessionOptions<Session>
): Promise<Session | null>
export async function getFreshBrowserSession(
  supabase: BrowserAuthSessionClient,
  options: FreshBrowserSessionOptions
): Promise<BrowserAuthSession | null>
export async function getFreshBrowserSession(
  supabase: BrowserAuthSessionClient,
  options: FreshBrowserSessionOptions = {}
) {
  const currentSession = hasExplicitSession(options)
    ? options.session ?? null
    : await readStoredSession(supabase, options.onError)

  if (!currentSession?.access_token?.trim()) {
    return null
  }

  if (!shouldRefreshBrowserSession(currentSession)) {
    return currentSession
  }

  const refreshedSession = await refreshStoredSession(
    supabase,
    options.onError
  )

  if (!refreshedSession?.access_token?.trim()) {
    return null
  }

  if (shouldRefreshBrowserSession(refreshedSession)) {
    return null
  }

  return refreshedSession
}

// --------------

export async function getFreshBrowserAccessToken(
  supabase: BrowserAuthSessionClient,
  options: FreshBrowserSessionOptions = {}
) {
  const session = await getFreshBrowserSession(supabase, options)

  return session?.access_token?.trim() || null
}

// --------------

export function resetFreshBrowserSessionForTests() {
  pendingStoredSessionRefresh = null
}
